/* eslint-disable jsx-a11y/anchor-is-valid */
import clsx from 'clsx'
import React, {useEffect, useState} from 'react'
import * as Yup from 'yup'
import {Formik} from 'formik'
import useUtils from './useUtils'
import {useLocation, useNavigate} from 'react-router-dom'
import {setTitle} from '../../redux/reducers/mainSlice'
import {useDispatch} from 'react-redux'
import {KTSVG} from '../../../_metronic/helpers'
import {FaTimes} from 'react-icons/fa'
import AssignConfirmation from '../../modules/alert/AssignConfirmation'
import {resizeImage, validateImageFile} from '../../utils/imageUtils'

const AddAnnouncementType = () => {
  const {updateWithNewMediaId, updateDataOnly, submitAnnouncementType} = useUtils()
  const location = useLocation()
  const dispatch = useDispatch()
  const locationState = location?.state?.values || null
  dispatch(setTitle('Add Type'))
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [isResizingImage, setIsResizingImage] = useState(false)

  const initialValues = locationState
    ? {
        id: locationState?.id,
        isEdit: true,
        mediaId: locationState.mediaId,
        iconImage: null,
        type: locationState?.type,
        imageUrl: locationState?.imageUrl,
      }
    : {
        type: '',
        mediaId: '',
        iconImage: null,
        imageUrl: null,
      }

  const handleFileChange = async (e, setFieldValue) => {
    const file = e.currentTarget.files?.[0]
    if (!file) return

    try {
      setIsResizingImage(true)
      
      // Validate the file
      validateImageFile(file)
      
      // Resize the image with max dimension of 590
      const resizedFile = await resizeImage(file, 590)
      
      // Set the resized file in the form
      setFieldValue('iconImage', resizedFile)
      setFieldValue('mediaId', null)
    } catch (error) {
      console.error('Error processing image:', error)
      alert(`Error processing image: ${error.message}`)
    } finally {
      setIsResizingImage(false)
      // Clear the input to allow selecting the same file again
      e.target.value = ''
    }
  }

  const validationSchema = Yup.object().shape({
    type: Yup.string().required('Type is required'),
    iconImage: Yup.mixed().when('imageUrl', {
      is: (imageUrl) => !imageUrl, // Apply validation if imgUrl is null or empty
      then: Yup.mixed().required('Image is required'), // Image validation is required if imgUrl is null
    }),
  })

  const handleSubmit = async (values) => {
    if (values.isEdit) {
      if (values.mediaId) {
        updateDataOnly(values)
      } else {
        updateWithNewMediaId(values)
      }
    } else {
      submitAnnouncementType(values)
    }
  }

  return (
    <>
      <div className='card card-custom'>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({values, touched, handleChange, errors, handleBlur, handleSubmit, setFieldValue}) => {
            let image = ''
            if (values.isEdit) {
              if (values.iconImage) {
                image = values.iconImage ? URL.createObjectURL(values.iconImage) : ''
              } else {
                image = values.imageUrl
              }
            } else {
              image = values.iconImage ? URL.createObjectURL(values.iconImage) : ''
            }
            return (
              <form className='form' onSubmit={handleSubmit}>
                <div className='card-body'>
                  <div className='tab-content pt-3'>
                    <div className='row align-items-center flex-wrap'>
                      <div className='col-lg-8'>
                        <label className='col-form-label required fw-bold fs-6'>Type</label>
                        <input
                          type='text'
                          className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                          placeholder='Icon type'
                          name='type'
                          value={values.type}
                          onChange={handleChange}
                        />
                        {touched.type && errors.type && (
                          <div className='fv-plugins-message-container'>
                            <span role='alert' className='text-danger'>
                              {errors.type}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='d-flex align-items-start mt-8 gap-10 flex-wrap'>
                      <div>
                        <label className='col-form-label required fw-bold fs-6'>Icon Image</label>
                        {!values.iconImage && !values.imageUrl ? (
                          <div>
                            <div
                              className='d-flex flex-column justify-content-center align-items-center bg-light-primary px-6 py-8 rounded-2 mb-7'
                              onClick={() => !isResizingImage && document.getElementById('fileInput')?.click()}
                              style={{
                                cursor: isResizingImage ? 'not-allowed' : 'pointer',
                                width: '400px',
                                height: '200px',
                              }}
                            >
                              {isResizingImage ? (
                                <>
                                  <div className='spinner-border text-primary' role='status'>
                                    <span className='visually-hidden'>Loading...</span>
                                  </div>
                                  <span className='text-primary fw-semibold fs-6 mt-2'>Resizing image...</span>
                                </>
                              ) : (
                                <>
                                  <KTSVG
                                    path='/media/icons/duotune/arrows/arr075.svg'
                                    className='svg-icon-3x svg-icon-primary d-block my-2'
                                  />
                                  <a href='#' className='text-primary fw-semibold fs-6'>
                                    Upload Icon
                                  </a>
                                </>
                              )}
                            </div>
                            <input
                              id='fileInput'
                              type='file'
                              accept='.jpg, .jpeg, .png'
                              style={{display: 'none'}}
                              disabled={isResizingImage}
                              onChange={(e) => handleFileChange(e, setFieldValue)}
                            />
                            {touched.iconImage && errors.iconImage && (
                              <div className='fv-plugins-message-container'>
                                <span role='alert' className='text-danger'>
                                  {errors.iconImage}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div
                            className='mb-8'
                            style={{position: 'relative', width: '400px', height: '200px'}}
                          >
                            <img src={image} style={{width: '400px', height: '200px'}} />
                            <div
                              className='rounded-3 p-1'
                              style={{
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                background: 'white',
                              }}
                            >
                              <FaTimes
                                style={{
                                  color: 'black',
                                  cursor: 'pointer',
                                  fontSize: '18px',
                                  backgroundPosition: 'center center',
                                }}
                                onClick={() => {
                                  setFieldValue('iconImage', null)
                                  setFieldValue('imageUrl', null)
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Submit Buttons */}
                <div className='d-flex align-items-center justify-content-end my-5'>
                  <button
                    type='button'
                    className='btn btn-danger ms-2 mt-2 me-2'
                    onClick={() => navigate('/announcement-types')}
                  >
                    Cancel
                  </button>
                  <button type='button'   onClick={() => {
                    if (values?.isEdit) {
                      setIsOpen(true) // Open the confirmation modal
                    } else {
                      document.querySelector('form')?.requestSubmit() // Directly submit in create mode
                    }
                  }} className='btn btn-primary ms-2 mt-2 me-2'>
                    {!values.isEdit ? 'Submit' : 'Update'}
                  </button>
                </div>
              </form>
            )
          }}
        </Formik>
      </div>
      <AssignConfirmation
        isVisible={isOpen}
        message='Are you sure you want to update this Announcement Type?'
        onConfirm={() => {
          setIsOpen(false)
          setTimeout(() => {
            document.querySelector('form')?.requestSubmit() // Triggers form submission
          }, 0)
        }}
        onCancel={() => setIsOpen(false)}
      />
    </>
  )
}

export default AddAnnouncementType
